/**
 * Web Push subscription helpers (client side).
 *
 * Talks to /notifications/push/* and the active service worker registration.
 * All functions degrade gracefully when push is unsupported (older browsers,
 * iOS Safari tab that isn't installed, etc.).
 */
import { apiFetch } from "./api.ts";

export function pushSupported(): boolean {
	return (
		typeof window !== "undefined" &&
		"serviceWorker" in navigator &&
		"PushManager" in window &&
		"Notification" in window
	);
}

export function pushPermission(): NotificationPermission | "unsupported" {
	if (!pushSupported()) return "unsupported";
	return Notification.permission;
}

/** Already subscribed on this device? */
export async function isSubscribed(): Promise<boolean> {
	if (!pushSupported()) return false;
	const reg = await navigator.serviceWorker.ready;
	return !!(await reg.pushManager.getSubscription());
}

function urlBase64ToUint8Array(base64: string): BufferSource {
	const padding = "=".repeat((4 - (base64.length % 4)) % 4);
	const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
	const raw = atob(b64);
	const buffer = new ArrayBuffer(raw.length);
	const out = new Uint8Array(buffer);
	for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
	return out;
}

/** Request permission + subscribe + register with the server. Returns true on success. */
export async function subscribePush(): Promise<boolean> {
	if (!pushSupported()) return false;

	const permission = await Notification.requestPermission();
	if (permission !== "granted") return false;

	const { public_key } = await apiFetch<{ public_key: string | null }>(
		"/notifications/push/key",
	);
	if (!public_key) return false;

	const reg = await navigator.serviceWorker.ready;
	const sub =
		(await reg.pushManager.getSubscription()) ??
		(await reg.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: urlBase64ToUint8Array(public_key),
		}));

	const json = sub.toJSON() as {
		endpoint?: string;
		keys?: { p256dh?: string; auth?: string };
	};
	if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false;

	await apiFetch("/notifications/push/subscribe", {
		method: "POST",
		body: {
			endpoint: json.endpoint,
			keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
		},
	});
	return true;
}

export async function unsubscribePush(): Promise<void> {
	if (!pushSupported()) return;
	const reg = await navigator.serviceWorker.ready;
	const sub = await reg.pushManager.getSubscription();
	if (!sub) return;
	await apiFetch("/notifications/push/unsubscribe", {
		method: "POST",
		body: { endpoint: sub.endpoint },
	}).catch(() => undefined);
	await sub.unsubscribe().catch(() => undefined);
}
